import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Eye, ShieldCheck, AlertCircle, ChevronRight } from 'lucide-react'
import { assessmentsApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { verdictBadgeColor, verdictLabel, appCategoryLabel, formatDate, roleLabel } from '@/lib/utils'
import type { AssessmentListItem } from '@/types'

export default function ReviewQueue() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isAllowed = !!user && ['DPO', 'DIRIGENTE'].includes(user.role)
  const isDirigente = user?.role === 'DIRIGENTE'

  const { data: allPending, isLoading } = useQuery({
    queryKey: ['review-queue'],
    queryFn: () =>
      assessmentsApi.list({
        scope: 'school',
        review_status: 'PENDING',
        page: 1,
        page_size: 100,
      }),
    enabled: isAllowed,
  })

  // Two-step chain: the DPO validates items not yet validated; the Dirigente
  // authorizes items the DPO has already validated.
  const pending = (allPending ?? []).filter((item) =>
    isDirigente ? !!item.dpo_validated_at : !item.dpo_validated_at
  )
  const stageLabel = isDirigente ? 'Da autorizzare' : 'Da validare'
  const stageHint = isDirigente
    ? 'Valutazioni già validate dal DPO, in attesa della tua autorizzazione.'
    : 'Valutazioni analizzate dall\'IA, in attesa della tua validazione come DPO.'

  if (!isAllowed) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="border-destructive/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <p>{t('audit.accessRestricted')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-giallo flex items-center justify-center">
          <Eye className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">{t('reviewQueue.title')}</h1>
          <p className="text-muted-foreground text-sm">{stageHint}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : pending.length > 0 ? (
        <div className="space-y-3">
          {pending.map((item: AssessmentListItem) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{item.app.name}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <Badge className="text-xs bg-mim-blue text-white">
                        {stageLabel}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {appCategoryLabel(item.app.category)}
                      </Badge>
                      {item.verdict && (
                        <Badge className={verdictBadgeColor(item.verdict)}>
                          {verdictLabel(item.verdict, i18n.language)}
                        </Badge>
                      )}
                      {item.overall_score && (
                        <span className="text-xs text-muted-foreground">
                          {Number(item.overall_score).toFixed(1)}/5
                        </span>
                      )}
                      {item.user && (
                        <span className="text-xs text-muted-foreground">
                          {t('reviewQueue.author')}: {item.user.name} ({roleLabel(item.user.role)})
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDate(item.created_at, i18n.language)}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/assessment/${item.id}`)}
                    className="bg-mim-blue hover:bg-blue-800 text-white flex-shrink-0"
                  >
                    {t('reviewQueue.examine')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-10 pb-10 text-center">
            <ShieldCheck className="w-10 h-10 text-verde mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{t('reviewQueue.empty')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
